import { apiFetch } from '@/lib/api';

export interface EnrichmentData {
  agent_notes: string[];
  enriched_fields: string[];
  timestamp: string;
}

export interface EnrichmentHistoryEntry {
  status: 'enriched' | 'failed';
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  source: string;
  agent_notes: string[];
  enriched_fields: string[];
  error: string | null;
}

export interface TripAdvisorReview {
  title: string;
  text: string;
  rating: number;
  date: string;
  author: string;
}

export interface Venue {
  id: string;
  organization_id: string;
  region?: string;
  country?: string;
  state?: string;
  city?: string;
  property_name: string;
  website?: string;
  address?: string;
  number_of_rooms?: number;
  meeting_spaces?: number;
  maximum_capacity?: number;
  star_rating?: number;
  lat?: number;
  lon?: number;
  place_id?: string;
  nearest_airport_iata?: string;
  nearest_airport_distance_km?: number;
  nearest_airport_name?: string;
  travel_time_minutes?: number;
  nearby_airports?: Array<{ iata: string; name: string; distance_km: number; rank: number; type: string }>;
  // Amenities
  number_of_restaurants?: number;
  number_of_bars?: number;
  number_of_pools?: number;
  spa_facilities?: boolean;
  beach_access?: 'beachfront' | 'beach_access' | 'none';
  all_inclusive_available?: boolean;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed';
  golf_course?: 'onsite' | 'nearby' | 'none';
  // TripAdvisor
  tripadvisor_link?: string;
  tripadvisor_rating?: number;
  tripadvisor_review_count?: number;
  tripadvisor_reviews_json?: TripAdvisorReview[];
  // Enrichment metadata
  enrichment_status: 'pending' | 'enriched' | 'failed' | 'none';
  enrichment_attempted_at?: string;
  enrichment_completed_at?: string;
  enrichment_error?: string;
  enrichment_source?: string;
  enrichment_data_json?: string | EnrichmentData; // JSON string or parsed object (Knex auto-parses JSONB)
  enrichment_history_json?: string | EnrichmentHistoryEntry[]; // JSON string or parsed array (Knex auto-parses JSONB)
  // Active enrichment job info (from jobs table join)
  enrichment_job_status?: 'queued' | 'processing' | null;
  enrichment_job_started_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface VenueFilters {
  city?: string;
  country?: string;
  state?: string;
  min_rooms?: number;
  max_capacity?: number;
  meeting_spaces_min?: number;
  star_rating?: number;
  nearest_airport_distance_max?: number;
  enrichment_status?: 'pending' | 'enriched' | 'failed' | 'none';
  enrichment_freshness_bucket?: '30d+' | '90d+' | '6mo+' | '1y+';
  // Amenity filters
  has_spa?: boolean;
  beach_access?: 'beachfront' | 'beach_access' | 'none';
  all_inclusive?: boolean;
  property_type?: 'adults_only' | 'family_friendly' | 'mixed';
  has_golf?: boolean;
  min_tripadvisor_rating?: number;
  search?: string;
  sort_by?: string;
  sort_dir?: 'asc' | 'desc';
  ungeolocated?: boolean;
}

export interface VenueImportResult {
  success: boolean;
  processed: number;
  inserted: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}

export interface VenueListResponse {
  items: Venue[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export class VenuesService {
  async list(page = 1, pageSize = 50, filters: VenueFilters = {}): Promise<VenueListResponse> {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(pageSize),
      ...Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
      )
    });

    const response = await apiFetch(`/api/venues?${params}`);
    if (!response.ok) throw new Error('Failed to fetch venues');
    
    const data = await response.json();
    return data.data;
  }

  async getFilterCities(search?: string): Promise<string[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await apiFetch(`/api/venues/filters/cities${params}`);
    if (!response.ok) throw new Error('Failed to fetch cities');
    
    const data = await response.json();
    return data.data;
  }

  async getFilterCountries(search?: string): Promise<string[]> {
    const params = search ? `?search=${encodeURIComponent(search)}` : '';
    const response = await apiFetch(`/api/venues/filters/countries${params}`);
    if (!response.ok) throw new Error('Failed to fetch countries');
    
    const data = await response.json();
    return data.data;
  }

  async getUngeolocatedCount(): Promise<number> {
    const response = await apiFetch('/api/venues/stats/ungeolocated-count');
    if (!response.ok) throw new Error('Failed to fetch ungeolocated count');
    
    const data = await response.json();
    return data.data.count;
  }

  async getById(id: string): Promise<Venue> {
    const response = await apiFetch(`/api/venues/${id}`);
    if (!response.ok) throw new Error('Failed to fetch venue');
    
    const data = await response.json();
    return data.data;
  }

  async create(venue: Omit<Venue, 'id' | 'organization_id' | 'created_at' | 'updated_at' | 'enrichment_status' | 'lat' | 'lon' | 'nearest_airport_iata' | 'nearest_airport_distance_km' | 'enrichment_attempted_at' | 'enrichment_completed_at' | 'enrichment_error' | 'enrichment_source'>): Promise<{ id: string }> {
    const response = await apiFetch('/api/venues', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(venue)
    });

    if (!response.ok) throw new Error('Failed to create venue');
    
    const data = await response.json();
    return data.data;
  }

  async update(id: string, updates: Partial<Venue>): Promise<void> {
    const response = await apiFetch(`/api/venues/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates)
    });

    if (!response.ok) throw new Error('Failed to update venue');
  }

  async delete(id: string): Promise<void> {
    const response = await apiFetch(`/api/venues/${id}`, {
      method: 'DELETE'
    });

    if (!response.ok) throw new Error('Failed to delete venue');
  }

  async importCSV(file: File): Promise<VenueImportResult> {
    const formData = new FormData();
    formData.append('csv', file);

    const response = await apiFetch('/api/venues/import', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) throw new Error('Failed to import CSV');
    
    const data = await response.json();
    return data.data;
  }

  async triggerEnrichment(id: string): Promise<void> {
    const response = await apiFetch(`/api/venues/${id}/enrich`, {
      method: 'POST'
    });

    if (!response.ok) throw new Error('Failed to trigger enrichment');
  }

  async resetEnrichment(id: string): Promise<void> {
    const response = await apiFetch(`/api/venues/${id}/reset-enrichment`, {
      method: 'POST'
    });

    if (!response.ok) throw new Error('Failed to reset enrichment');
  }

  async bulkAction(
    action: 'delete' | 'enrich' | 'refresh_airports',
    selection: { allFiltered: boolean; ids?: string[] },
    filter?: VenueFilters
  ): Promise<{ processed: number; message: string }> {
    const response = await apiFetch('/api/venues/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action, selection, filter })
    });

    if (!response.ok) throw new Error(`Failed to perform bulk ${action}`);
    
    const data = await response.json();
    return data.data;
  }

  async exportCSV(params: URLSearchParams): Promise<Response> {
    const response = await apiFetch(`/api/venues/export.csv?${params}`);
    if (!response.ok) throw new Error('Failed to export CSV');
    return response;
  }
}
